import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SignupFormData, ValidationState, FormErrors, BusinessNameValidationState, NetworkSelectionState } from '../types';
import { validateFCANumber } from '../services/fcaValidation';
import { checkFCAExistence, formatExistingFirms } from '../services/fcaExistenceCheck';
import { debouncedCheckBusinessName, nameCheckAllowsProgress } from '../services/businessNameValidation';
import { triggerOnboarding, isOnboardingEnabled } from '../services/onboardingService';
import { trackNetworkSelection } from '../services/networkService';
import { validateForm, sanitizeFormData, debounce, isValidEmail, isValidUKPhone } from '../utils/validation';
import { useRecaptcha } from './useRecaptcha';
import { useSecurityEnhancements } from './useSecurityEnhancements';
import { trackStepCompletionFull } from '../services/hubspotService';
import { saveFormProgress } from '../services/formStorageService';

const initialFormData: SignupFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  fcaNumber: '',
  icoNumber: '',
  companyName: '',
  companiesHouseNumber: '',
  desiredSubdomain: '',
  jobTitle: '',
  companySize: '',
  networkIds: [],
  networkNames: [],
  otherNetworkName: '',
  totangoId: '', // Temporary field for testing
  acceptTerms: false,
  acceptMarketing: false,
  trialTermsAccepted: false,
  privacyAccepted: false,
};

const initialValidationState: ValidationState = {
  isValidating: false,
  isValid: null,
  message: '',
  companyData: null,
};

const initialBusinessNameValidationState: BusinessNameValidationState = {
  isValidating: false,
  isAvailable: null,
  status: null,
  message: '',
  businessName: '',
};

// Map URL paths to step names for HubSpot tracking
const PATH_TO_STEP_NAME: Record<string, string> = {
  '/signup/personal-info': 'personal_info',
  '/signup/contact-info': 'contact_info',
  '/signup/fca-number': 'fca_number',
  '/signup/business-info': 'business_info',
  '/signup/role-company-size': 'role_company_size',
  '/signup/platform-setup': 'platform_setup',
  '/signup/review': 'review',
};

// Map URL paths to step numbers
const PATH_TO_STEP_NUMBER: Record<string, number> = {
  '/signup/personal-info': 1,
  '/signup/contact-info': 2,
  '/signup/fca-number': 3,
  '/signup/business-info': 4,
  '/signup/role-company-size': 5,
  '/signup/platform-setup': 6,
  '/signup/review': 7,
};

export const useSignupForm = () => {
  const location = useLocation();
  const { executeRecaptchaToken } = useRecaptcha();
  const { honeypotFields, updateHoneypotField, getSecurityData, performSecurityChecks } = useSecurityEnhancements();
  const [formData, setFormData] = useState<SignupFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [fcaValidation, setFcaValidation] = useState<ValidationState>(initialValidationState);
  const [businessNameValidation, setBusinessNameValidation] = useState<BusinessNameValidationState>(initialBusinessNameValidationState);
  const [networkSelection, setNetworkSelection] = useState<NetworkSelectionState>({
    selectedNetworks: [],
    showConditionalMessage: false,
    conditionalAction: null,
    canProceed: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Save form progress to localStorage and track in HubSpot whenever we change paths
  useEffect(() => {
    const stepName = PATH_TO_STEP_NAME[location.pathname];
    const stepNumber = PATH_TO_STEP_NUMBER[location.pathname];

    if (stepName && stepNumber) {
      // Save to localStorage (debounced to avoid too many writes)
      const saveTimeout = setTimeout(() => {
        saveFormProgress(formData, stepNumber);
      }, 500);

      return () => clearTimeout(saveTimeout);
    }
  }, [location.pathname, formData]);


  // Business name validation function (defined first to avoid dependency issues)
  const validateBusinessName = useCallback(async (businessName: string) => {
    if (!businessName.trim()) {
      setBusinessNameValidation(initialBusinessNameValidationState);
      return;
    }

    // Basic format check before API call
    const cleanBusinessName = businessName.trim();
    if (cleanBusinessName.length < 2 || cleanBusinessName.length > 200) {
      setBusinessNameValidation({
        isValidating: false,
        isAvailable: false,
        status: null,
        message: 'Business name must be between 2-200 characters long',
        businessName: cleanBusinessName,
      });
      return;
    }

    // Check if name contains at least one alphanumeric character
    if (!/[a-zA-Z0-9]/.test(cleanBusinessName)) {
      setBusinessNameValidation({
        isValidating: false,
        isAvailable: false,
        status: null,
        message: 'Business name must contain at least one letter or number',
        businessName: cleanBusinessName,
      });
      return;
    }

    setBusinessNameValidation(prev => ({ 
      ...prev, 
      isValidating: true,
      businessName: cleanBusinessName
    }));

    try {
      const response = await debouncedCheckBusinessName(cleanBusinessName);
      const status = response.status ?? (response.available ? 'available' : 'taken');

      // Use the message the service produced. Overwriting it with a blanket
      // "already registered" is what made an unreachable backend look like a
      // name collision to the customer.
      setBusinessNameValidation({
        isValidating: false,
        isAvailable: response.available,
        status,
        message:
          status === 'available'
            ? 'Great! This business name is available.'
            : status === 'taken'
            ? 'This business name is already registered.'
            : response.message || "We couldn't check this name right now. Please try again.",
        businessName: response.businessName,
      });

    } catch (error) {
      setBusinessNameValidation({
        isValidating: false,
        isAvailable: false,
        status: 'check_failed',
        message: "We couldn't check this name right now. Please try again.",
        businessName: cleanBusinessName,
      });
    }
  }, []);

  // Debounced business name validation function
  const debouncedValidateBusinessName = useMemo(
    () => debounce(validateBusinessName, 800),
    [validateBusinessName]
  );

  // Helper function to convert company name to subdomain format
  const generateSubdomainFromCompanyName = useCallback((companyName: string): string => {
    return companyName
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters (including spaces)
      .substring(0, 63); // Limit to 63 characters (DNS subdomain limit)
  }, []);

  // FCA validation function
  const validateFCA = useCallback(async (fcaNumber: string) => {
    if (!fcaNumber.trim()) {
      setFcaValidation(initialValidationState);
      
      // Clear auto-filled data when FCA number is removed
      setFormData(prev => ({
        ...prev,
        companyName: '',
        companiesHouseNumber: '',
        // Only clear subdomain if it appears to be auto-generated
        desiredSubdomain: prev.companyName && prev.desiredSubdomain === generateSubdomainFromCompanyName(prev.companyName) ? '' : prev.desiredSubdomain
      }));
      
      return;
    }

    // Basic format check before API call
    const cleanFCA = fcaNumber.replace(/\s+/g, '');
    if (!/^\d{6,8}$/.test(cleanFCA)) {
      setFcaValidation({
        isValidating: false,
        isValid: false,
        message: 'FCA number must be 6-8 digits',
        companyData: null,
      });
      return;
    }

    setFcaValidation(prev => ({ ...prev, isValidating: true }));

    try {
      // First, validate the FCA number with the FCA registry
      const response = await validateFCANumber(fcaNumber);
      
      if (!response.valid) {
        setFcaValidation({
          isValidating: false,
          isValid: response.valid ?? null,
          message: response.message || 'Invalid FCA number',
          companyData: response.data || null,
        });
        return;
      }

      // If FCA validation succeeded, check if it already exists in our system
      const existenceCheck = await checkFCAExistence(fcaNumber);
      
      let validationMessage = response.message || 'Valid FCA number';
      
      // If the FCA number exists in our system, add a warning message
      if (existenceCheck.exists && existenceCheck.existing_firms.length > 0) {
        const firmsList = formatExistingFirms(existenceCheck.existing_firms);
        validationMessage = `⚠️ This FCA number is already used by: ${firmsList}. You can still proceed if this is a different firm or branch.`;
      }
      
      setFcaValidation({
        isValidating: false,
        isValid: response.valid ?? null,
        message: validationMessage,
        companyData: response.data || null,
      });

      // Auto-fill fields if validation is successful
      if (response.valid && response.data) {
        setFormData(prev => {
          const updates: Partial<SignupFormData> = {};

          // Always update company name if available from FCA response
          const organisationName = response.data?.organisationName || response.organisation_name;
          if (organisationName) {
            updates.companyName = organisationName;
          }
          
          // Always update companies house number if available from FCA response
          const companiesHouseNumber = response.data?.companiesHouseNumber || response.companies_house_number;
          if (companiesHouseNumber) {
            updates.companiesHouseNumber = companiesHouseNumber;
          }
          
          // Auto-suggest subdomain based on company name (only if current subdomain is empty or was auto-generated)
          if (organisationName) {
            const newSuggestedSubdomain = generateSubdomainFromCompanyName(organisationName);
            console.log('🏗️ FCA Auto-fill: Generated subdomain from company name:', {
              companyName: organisationName,
              generatedSubdomain: newSuggestedSubdomain,
              currentSubdomain: prev.desiredSubdomain,
              willUpdate: !prev.desiredSubdomain.trim() || 
                         (prev.companyName && prev.desiredSubdomain === generateSubdomainFromCompanyName(prev.companyName))
            });
            
            // Only update subdomain if it's empty or appears to be auto-generated from previous company name
            if (!prev.desiredSubdomain.trim() || 
                (prev.companyName && prev.desiredSubdomain === generateSubdomainFromCompanyName(prev.companyName))) {
              
              if (newSuggestedSubdomain.length >= 3) {
                updates.desiredSubdomain = newSuggestedSubdomain;
              }
            }
          }
          
          return { ...prev, ...updates };
        });

        // Note: Business name validation will be triggered automatically when the subdomain
        // is auto-filled (see line 342 in updateField). No need to trigger it here.
      }

    } catch (error) {
      setFcaValidation({
        isValidating: false,
        isValid: false,
        message: 'Failed to validate FCA number. Please try again.',
        companyData: null,
      });
    }
  }, [generateSubdomainFromCompanyName]);

  // Debounced FCA validation function
  const debouncedValidateFCA = useMemo(
    () => debounce(validateFCA, 800),
    [validateFCA]
  );

  // Handle form field updates
  const updateField = useCallback((field: keyof SignupFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError('');
    }

    // Handle multiple network selections
    if (field === 'networkIds' && Array.isArray(value)) {
      const { processMultipleNetworkSelection } = require('../services/networkService');
      const selectionState = processMultipleNetworkSelection(value);
      setNetworkSelection(selectionState);
      
      // Track analytics for new selections
      value.forEach(networkId => {
        if (typeof networkId === 'string') {
          trackNetworkSelection(networkId, 'selected');
        }
      });
    }

    // Trigger FCA validation when FCA number changes
    if (field === 'fcaNumber' && typeof value === 'string') {
      debouncedValidateFCA(value);
    }

    // Trigger DNS validation when desired subdomain changes
    if (field === 'desiredSubdomain' && typeof value === 'string') {
      debouncedValidateBusinessName(value);
    }

    // Real-time email validation
    if (field === 'email' && typeof value === 'string') {
      if (value.trim() && !isValidEmail(value.trim())) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }

    // Real-time phone validation
    if (field === 'phone' && typeof value === 'string') {
      if (value.trim() && !isValidUKPhone(value.trim())) {
        setErrors(prev => ({ ...prev, phone: 'Please enter a valid UK phone number (e.g., 07123456789 or +44 7123 456789)' }));
      } else {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
    }

    // Auto-fill subdomain when company name changes (if subdomain is empty or was auto-generated)
    if (field === 'companyName' && typeof value === 'string') {
      setFormData(prev => {
        const suggestedSubdomain = generateSubdomainFromCompanyName(value);

        // Only update subdomain if it's empty or appears to be auto-generated from previous company name
        if ((!prev.desiredSubdomain.trim() ||
             (prev.companyName && prev.desiredSubdomain === generateSubdomainFromCompanyName(prev.companyName))) &&
            suggestedSubdomain.length >= 3) {

          // Subdomain validation will be triggered automatically by the field update
          // via the debounced validation in line 333-335
          return { ...prev, [field]: value, desiredSubdomain: suggestedSubdomain };
        }

        return { ...prev, [field]: value };
      });
      return;
    }
  }, [errors, submitError, debouncedValidateFCA, debouncedValidateBusinessName, generateSubdomainFromCompanyName]);

  // Validate entire form
  const validateFullForm = useCallback((): boolean => {
    const sanitizedData = sanitizeFormData(formData);
    const validationErrors = validateForm(sanitizedData);

    // Add FCA validation error if FCA number is invalid (but not if service is unavailable)
    if (fcaValidation.isValid === false) {
      validationErrors.fcaNumber = fcaValidation.message || 'Invalid FCA number';
    }

    // Add DNS validation error if the subdomain is genuinely unavailable.
    // A check we could not complete ('check_failed') must not block submission -
    // same rule already applied to FCA validation above. A true duplicate is
    // still caught downstream by the onboarding pipeline.
    if (businessNameValidation.isAvailable === false && businessNameValidation.status !== 'check_failed') {
      validationErrors.desiredSubdomain = businessNameValidation.message || 'Business name is not available';
    }

    // Debug logging
    console.log('🔍 validateFullForm: Form data:', sanitizedData);
    console.log('🔍 validateFullForm: Validation errors:', validationErrors);
    console.log('🔍 validateFullForm: Error values:', Object.entries(validationErrors).filter(([_, err]) => err));

    setErrors(validationErrors);
    return Object.values(validationErrors).every(err => !err); // Check if all error values are falsy
  }, [formData, fcaValidation, businessNameValidation]);

  // Handle form submission
  const handleSubmit = useCallback(async (onSuccess?: (data: SignupFormData) => void) => {
    console.log('🚀 useSignupForm: handleSubmit called');
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Validate form
      console.log('🔍 useSignupForm: Validating form...');
      if (!validateFullForm()) {
        console.warn('⚠️ useSignupForm: Form validation failed');
        setIsSubmitting(false);
        return false;
      }
      console.log('✅ useSignupForm: Form validation passed');

      // Ensure FCA number is valid (allow proceeding if service is unavailable)
      if (fcaValidation.isValid === false) {
        setSubmitError('Please provide a valid FCA number');
        setIsSubmitting(false);
        return false;
      }

      // If FCA service is unavailable (isValid === null), allow proceeding with a warning
      if (fcaValidation.isValid === null && formData.fcaNumber.trim()) {
        console.warn('Proceeding with unvalidated FCA number due to service unavailability');
      }

      // Only a confirmed conflict blocks submission. canSubmit already lets a
      // failed check through, so testing isAvailable here enabled the button
      // and then told the customer their subdomain was taken.
      if (!nameCheckAllowsProgress(businessNameValidation)) {
        setSubmitError('Please choose an available subdomain');
        setIsSubmitting(false);
        return false;
      }

      // Sanitize form data
      console.log('🧹 useSignupForm: Sanitizing form data...');
      const sanitizedData = sanitizeFormData(formData);

      // Perform client-side security checks
      console.log('🔒 useSignupForm: Performing security checks...');
      const securityCheckResult = performSecurityChecks();
      if (!securityCheckResult.passed) {
        const errorMessage = securityCheckResult.issues.length > 0
          ? securityCheckResult.issues[0]
          : 'Security validation failed';
        console.error('❌ useSignupForm: Security checks failed:', errorMessage);
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return false;
      }
      console.log('✅ useSignupForm: Security checks passed');

      // Execute reCAPTCHA challenge before submission
      console.log('🤖 useSignupForm: Executing reCAPTCHA...');
      let recaptchaToken: string | null = null;
      try {
        recaptchaToken = await executeRecaptchaToken('form_submit');
        if (!recaptchaToken) {
          console.warn('⚠️ reCAPTCHA token not available - proceeding with submission');
        } else {
          console.log('✅ useSignupForm: reCAPTCHA token obtained');
        }
      } catch (error) {
        console.error('❌ reCAPTCHA execution failed:', error);
        // Continue without token - reCAPTCHA failure shouldn't block legitimate users
      }

      // Get security data
      console.log('📊 useSignupForm: Getting security data...');
      const securityData = getSecurityData();

      // Simulate API call (replace with actual submission logic)
      console.log('⏳ useSignupForm: Simulating API call (2 second delay)...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('✅ useSignupForm: API simulation complete');

      // Trigger onboarding pipeline if enabled
      console.log('🔍 useSignupForm: Checking if onboarding is enabled:', isOnboardingEnabled());
      if (isOnboardingEnabled()) {
        console.log('🚀 useSignupForm: Triggering onboarding pipeline...');
        try {
          const onboardingResult = await triggerOnboarding(
            sanitizedData,
            recaptchaToken,
            securityData.formStartTime,
            securityData.formSubmitTime,
            securityData.browserFeatures,
            securityData.honeypotFields
          );
          if (onboardingResult.success) {
            console.log('✅ useSignupForm: Onboarding pipeline triggered successfully:', onboardingResult);
          } else {
            console.warn('⚠️ useSignupForm: Onboarding pipeline failed:', onboardingResult.error);
            // Note: We don't fail the form submission if onboarding fails
            // This ensures user gets confirmation even if backend onboarding has issues
          }
        } catch (error) {
          console.error('❌ useSignupForm: Error triggering onboarding:', error);
          // Continue with form submission success - onboarding can be triggered manually if needed
        }
      } else {
        console.log('⏭️ useSignupForm: Onboarding disabled, skipping pipeline trigger');
      }

      // Success - Track completion in HubSpot
      console.log('📊 useSignupForm: Tracking completion in HubSpot...');
      trackStepCompletionFull('completed', sanitizedData);

      console.log('✅ useSignupForm: Setting success state');
      setIsSuccess(true);
      if (onSuccess) {
        console.log('✅ useSignupForm: Calling onSuccess callback');
        onSuccess(sanitizedData);
      }

      console.log('✨ useSignupForm: Form submission complete, returning true');
      return true;

    } catch (error) {
      setSubmitError('Failed to submit form. Please try again.');
      console.error('Form submission error:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, fcaValidation, businessNameValidation, validateFullForm, executeRecaptchaToken, getSecurityData, performSecurityChecks]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setFcaValidation(initialValidationState);
    setBusinessNameValidation(initialBusinessNameValidationState);
    setNetworkSelection({
      selectedNetworks: [],
      showConditionalMessage: false,
      conditionalAction: null,
      canProceed: true
    });
    setIsSubmitting(false);
    setSubmitError('');
    setIsSuccess(false);
  }, []);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData]);

  // Auto-validate FCA number on mount if it exists
  useEffect(() => {
    if (formData.fcaNumber.trim()) {
      debouncedValidateFCA(formData.fcaNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Auto-validate subdomain on mount if it exists
  useEffect(() => {
    if (formData.desiredSubdomain.trim()) {
      debouncedValidateBusinessName(formData.desiredSubdomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Helper function to update multiple networks
  const updateNetworks = useCallback((networkIds: string[], networkNames: string[]) => {
    updateField('networkIds', networkIds);
    updateField('networkNames', networkNames);
  }, [updateField]);

  // Callback for when a step is completed - triggers HubSpot tracking
  const onStepComplete = useCallback((stepName: string) => {
    console.log('📍 Step completed:', stepName);
    // Track step completion in HubSpot with current form data
    trackStepCompletionFull(stepName, formData);

    // Save progress immediately on step completion
    const stepNumber = PATH_TO_STEP_NUMBER[`/signup/${stepName.replace(/_/g, '-')}`];
    if (stepNumber) {
      saveFormProgress(formData, stepNumber);
    }
  }, [formData]);

  return {
    // Form data
    formData,
    errors,
    fcaValidation,
    businessNameValidation,
    networkSelection,
    isSubmitting,
    submitError,
    isSuccess,

    // Security enhancements
    honeypotFields,
    updateHoneypotField,

    // Actions
    updateField,
    updateNetworks,
    handleSubmit,
    resetForm,
    validateFullForm,
    onStepComplete,

    // Computed properties
    hasUnsavedChanges,
    canSubmit: !isSubmitting &&
                (fcaValidation.isValid === true || fcaValidation.isValid === null) && // Allow submission when service is unavailable
                nameCheckAllowsProgress(businessNameValidation) && // Allow submission when the name check is unavailable
                Object.values(errors).every(err => !err), // Check if all error values are falsy (empty string = no error)
    isFormValid: Object.values(errors).every(err => !err) && // Check if all error values are falsy
                  (fcaValidation.isValid === true || fcaValidation.isValid === null) && // Allow validation when service is unavailable
                  nameCheckAllowsProgress(businessNameValidation),
  };
};
